import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, Table,
  TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { cloError, cloGet, cloPatch, cloPost, ENDPOINTS } from "./api";

const EMPTY_FORM = {
  displayName: "",
  externalKey: "",
  logoUrl: "",
  providerCategoryId: "",
  contractRef: "",
  billingContact: "",
  visaExternalId: "",
  pendingPeriodDays: "90",
  status: "enabled",
};

const RetailersTab: React.FC = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<any>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_RETAILERS, { page: page + 1, pageSize, search: search || undefined });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
      setCategories(res.metadata?.providerCategories || []);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, search, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing({});
  };

  const openEdit = (row: any) => {
    setForm({
      displayName: row.display_name,
      externalKey: row.external_key,
      logoUrl: row.logo_url || "",
      providerCategoryId: row.provider_category_id ? String(row.provider_category_id) : "",
      contractRef: row.contract_ref || "",
      billingContact: row.billing_contact || "",
      visaExternalId: row.visa_external_id || "",
      pendingPeriodDays: String(row.pending_period_days),
      status: row.status,
    });
    setEditing(row);
  };

  const save = async () => {
    setSaving(true);
    const body: any = {
      displayName: form.displayName,
      logoUrl: form.logoUrl || null,
      providerCategoryId: form.providerCategoryId ? Number(form.providerCategoryId) : null,
      contractRef: form.contractRef || null,
      billingContact: form.billingContact || null,
      visaExternalId: form.visaExternalId.trim().toUpperCase() || null,
      pendingPeriodDays: Number(form.pendingPeriodDays),
    };
    try {
      if (editing?.brand_id) {
        await cloPatch(`${ENDPOINTS.CLO_RETAILERS}/${editing.brand_id}`, { ...body, status: form.status });
        notify("Retailer updated", "info");
      } else {
        await cloPost(ENDPOINTS.CLO_RETAILERS, { ...body, externalKey: form.externalKey });
        notify("Retailer created", "info");
      }
      setEditing(null);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    } finally {
      setSaving(false);
    }
  };

  const field = (key: string, label: string, props: any = {}) => (
    <TextField fullWidth margin="dense" label={label} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />
  );

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
        <TextField size="small" variant="outlined" label="Search retailers" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="contained" color="primary" onClick={openCreate}>
          Add retailer
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Brand ID</TableCell>
            <TableCell>Retailer</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Contract</TableCell>
            <TableCell>Visa ID</TableCell>
            <TableCell>Pending days</TableCell>
            <TableCell>MIDs</TableCell>
            <TableCell>Campaigns</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.brand_id}>
              <TableCell>{r.brand_id}</TableCell>
              <TableCell>
                <Typography variant="body2">{r.display_name}</Typography>
                <Typography variant="caption" color="textSecondary">{r.external_key}</Typography>
              </TableCell>
              <TableCell>{r.provider_category || "—"}</TableCell>
              <TableCell>{r.contract_ref || "—"}</TableCell>
              <TableCell>{r.visa_external_id || "—"}</TableCell>
              <TableCell>{r.pending_period_days}</TableCell>
              <TableCell>{r.mid_count}</TableCell>
              <TableCell>{r.campaign_count}</TableCell>
              <TableCell>
                <Chip size="small" label={r.status} color={r.status === "enabled" ? "primary" : "default"} />
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center">No CLO retailers yet</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.brand_id ? `Edit ${editing.display_name}` : "Add CLO retailer"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>{field("displayName", "Name")}</Grid>
            <Grid item xs={6}>{field("externalKey", "Key (e.g. tesco)", { disabled: Boolean(editing?.brand_id) })}</Grid>
            <Grid item xs={12}>{field("logoUrl", "Logo URL", { helperText: "Full link starting with https://, or leave empty" })}</Grid>
            <Grid item xs={6}>
              {field("providerCategoryId", "Category", {
                select: true,
                children: [<MenuItem key="none" value="">None</MenuItem>, ...categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.provider_category} → {c.category_name}</MenuItem>
                ))],
              })}
            </Grid>
            <Grid item xs={6}>{field("contractRef", "Contract reference")}</Grid>
            <Grid item xs={6}>{field("billingContact", "Billing contact")}</Grid>
            <Grid item xs={6}>{field("visaExternalId", "Visa external ID", { helperText: "Our ID for this retailer in Visa. Capital letters and digits, up to 25" })}</Grid>
            <Grid item xs={6}>{field("pendingPeriodDays", "Pending period (days)", { type: "number", helperText: "Days a reward waits before it is paid. A change applies to each campaign from its next approved version." })}</Grid>
            {editing?.brand_id && (
              <Grid item xs={6}>
                {field("status", "Status", {
                  select: true,
                  children: [<MenuItem key="e" value="enabled">enabled</MenuItem>, <MenuItem key="d" value="disabled">disabled</MenuItem>],
                })}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={saving} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RetailersTab;
